import { useLocation } from "react-router-dom";
import Header from "./Header";
import BottomNav from "./BottomNav";
import CookieBanner from "./CookieBanner";
import FeedComposer from "@/components/feed/FeedComposer";
import { useSTXTheme } from "@/contexts/STXThemeContext";
import DesktopSidebar from "./DesktopSidebar";
import DesktopSuggestions from "./DesktopSuggestions";
import ProfileCompletenessBanner from "@/components/home/ProfileCompletenessBanner";

const NO_SHELL = ["/login", "/register", "/onboarding/profile", "/forgot-password", "/reset-password", "/admin", "/impersonate", "/stories/viewer"];

// STX-Bereich (dark/light schaltbar): Shell muss die STX-Palette selbst kennen,
// sonst schimmert der weiße Shell-Hintergrund durch, wo der Content endet.
const STX_ROUTES = ["/stilltouchedx"];
function isSTXRoute(pathname) {
  if (pathname.startsWith("/user/") && pathname.endsWith("/stx")) return true;
  return STX_ROUTES.some((r) => pathname.startsWith(r));
}

// iter120 · Message 960:
// Sub-page-Routen der linken Sidebar (unterhalb MP/STX) blenden die rechte Spalte aus
// und dehnen den Content auf die volle Breite (Mittel + Rechts).
// Betrifft ALLE Unterseiten dieser Pfade.
const NO_RIGHT_COL_PREFIXES = [
  "/notifications",
  "/messages",
  "/bookmarks",
  "/subscriptions",
  "/profile",
  "/settings",
  "/studio",
  "/orders",
  "/checkout",
  "/cart",
  "/saved-posts",
  "/saved-searches",
  "/edit-profile",
  "/watchlist",
];
function hideRightColumn(pathname) {
  return NO_RIGHT_COL_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const { mode: stxMode } = useSTXTheme();
  const hideShell = NO_SHELL.some(p => pathname.startsWith(p));
  const isStx = isSTXRoute(pathname);

  // Admin pages get full-width layout without mobile shell
  if (pathname.startsWith("/admin")) return <div className="min-h-screen bg-[#FAFAFA]">{children}</div>;

  if (hideShell) return <div className="min-h-screen bg-white">{children}<CookieBanner /></div>;

  // STX-Routes: Wir vergeben .stx-theme (Dark oder Light) an den gesamten Shell,
  // damit die CSS-Variablen (--stx-bg …) bis zum viewport-Rand gelten und kein
  // Weiß mehr unter dem Content-Ende hervorblitzt.
  const stxClass = isStx ? `stx-theme ${stxMode === "light" ? "stx-light" : ""}` : "";
  const outerBg = isStx ? "" : "bg-white";
  const stxSurface = isStx ? { backgroundColor: "var(--stx-bg)" } : undefined;

  // iter120 · Message 960: Grid-Cols abhängig davon, ob rechte Spalte gezeigt wird.
  const hideRight = hideRightColumn(pathname);
  const gridCols = hideRight
    ? "2xl:grid-cols-[240px_minmax(0,1fr)]"
    : "2xl:grid-cols-[240px_minmax(0,1fr)_288px]";

  return (
    <div className={`min-h-screen flex flex-col ${outerBg} ${stxClass}`} style={stxSurface}>
      <Header />
      <ProfileCompletenessBanner />
      <div
        className={`flex-1 w-full max-w-[1280px] mx-auto grid grid-cols-1 ${gridCols}`}
        style={stxSurface}
      >
        <DesktopSidebar />
        <main
          className="min-w-0 pb-20 2xl:pb-4 w-full"
          style={stxSurface}
          data-testid="app-shell-main"
        >
          {children}
        </main>
        {!hideRight && <DesktopSuggestions />}
      </div>
      <div className="2xl:hidden"><BottomNav /></div>
      <CookieBanner />
      <FeedComposer />
    </div>
  );
}
